import {RootState} from "../../../store/store";
import {setIsLoading} from "../../../store/actions/dateRange.actions";
import {AlertTypes, apiClient, Grid, IAlertData, setAlert, Paper, Typography, Skeleton, useTheme, MuiIcons} from "@leap4/frontend";
import {connect, ConnectedProps} from "react-redux";
import {IDateRangeGState, ILoadingComponents} from "../../../interfaces/datesGlobalState.interface";
import {ITopTagsRangeCombo} from "../home/interfaces";
import {IPartnerOverview} from "./interfaces/IpartnerOverview";
import React, {useEffect, useState} from "react";
import {IOverviewGraphResponse} from "@leap4/entities-discrepancy-common";
import {getDefaultTimeDisplay, indexToTimeInterval} from "../common/charts/charts";
import {ComparisonState, partnersBaseUrl} from "../../../config/generals";
import {useAppDispatch, useRefreshInterval} from "../../../store/hooks";
import {useParams} from "react-router-dom";
import {metricsComparisonKeyMap} from "../../shared/MetricsComparison/MetricsComparisonHelpers";
import {MetricsComparison} from "../../shared/MetricsComparison/MetricsComparison.container";
import {OverviewInfo, OverviewMetrics} from "../common/OverviewGeneralInfo/OverviewGeneralInfo";
import ComparisonOverviewGraph from "../common/ComparisonGraph";
import AdserverTagsTable from "../adServers/Overview/TagsTable/AdserverTagsTable";
import HomeTopTagsTable from "../home/components/topCombosTable/HomeTopTagsTable";
import {PredictiveGraphContainer} from "../common/PredictiveGraph/PredictiveGraph.container";
import ReportHeader from "../common/ReportHeader/ReportHeader";
import {urlService} from "../../routing/urlsService";
import {DataTable} from "./networkPartners/dataTable";
import moment from "moment";
import {checkIfDateIsInComparisonOptions, getComparisonOptionsDates} from "../common/time.utils";

const { Tag } = MuiIcons;

const mapStateToProps = (state: RootState) => ({
    dates: state.dates,
    selectedOrganization: state.auth.selectedOrganizationId
});

const mapActions = {
    setIsLoading,
    setAlert
}

const connector = connect(mapStateToProps, mapActions);

type PropsFromRedux = ConnectedProps<typeof connector>;

export interface IPropsPartnerOverview extends PropsFromRedux {
    dates: IDateRangeGState
    setIsLoading: (isLoading: Partial<ILoadingComponents>) => void
    setAlert: (alert: IAlertData) => void
}

export interface IPartnerOverviewResult {
    overview: IPartnerOverview,
    comboTags: ITopTagsRangeCombo
}

const PartnerOverview: React.FC<IPropsPartnerOverview> = ({
                                                              dates: {
                                                                  startDate,
                                                                  endDate,
                                                                  isLoading,
                                                                  selectedComparison
                                                              },
                                                              setIsLoading,
                                                              setAlert,
                                                              selectedOrganization
                                                          }): JSX.Element => {
    const [data, setData] = useState<IPartnerOverviewResult>(null);
    const theme = useTheme()
    const [graphData, setGraphData] = useState<IOverviewGraphResponse>(null)
    const [graphInterval, setGraphInterval] = useState<number>(getDefaultTimeDisplay(startDate, endDate))
    const [specificComparisonDate, setSpecificComparisonDate] = useState()
    const [optionalComparisonDates, setOptionalComparisonDate] = useState<any>(getComparisonOptionsDates(startDate, endDate))

    useEffect(() => {
        if (specificComparisonDate && !checkIfDateIsInComparisonOptions(specificComparisonDate, optionalComparisonDates)) {
            fetchGeneralData()
            fetchGraphData()
        }
    }, [specificComparisonDate])


    const [comparisonData, setComparisonData] = useState<ComparisonState>({
        compare: selectedComparison,
        data: null
    })
    useRefreshInterval(() => {
        if (startDate === endDate && startDate === moment().format("YYYY-MM-DD")) {
            fetchGeneralData()
            fetchGraphData()
        }
    })
    const params = useParams<{ id: string }>()
    const dispatch = useAppDispatch();

    // Component Computed Properties
    const partnerName = data?.overview?.partner.name

    // Component Methods
    const fetchGeneralData = () => {
        (async () => {
            try {
                setIsLoading({isLoadingGeneral: true});

                const baseUrl = `${partnersBaseUrl}/overview/${params.id}`;
                const apiResponse = await apiClient.post(baseUrl, {
                    startDate,
                    endDate,
                    specificComparisonDate: specificComparisonDate || null
                })
                const apiData: IPartnerOverviewResult = apiResponse.data
                setData(apiData)

            } catch (err) {
                setAlert({msg: "Couldn't fetch data", type: AlertTypes.FAILURE, d: dispatch})
            } finally {
                setIsLoading({isLoadingGeneral: false});
            }
        })()
    };

    const fetchGraphData = () => {
        (async () => {
            try {
                setIsLoading({isLoadingGraph: true});
                const baseUrl = `${partnersBaseUrl}/overview/${params.id}/graph`
                const graphResponse = await apiClient.post(baseUrl, {
                    startDate,
                    endDate,
                    specificComparisonDate: specificComparisonDate || null,
                    timeInterval: indexToTimeInterval(graphInterval)
                })
                const currentGraphData: IOverviewGraphResponse = graphResponse.data
                setGraphData(currentGraphData)
            } catch (err) {
                setAlert({msg: "Could'nt fetch Graph Data", type: AlertTypes.FAILURE, d: dispatch})
            } finally {
                setIsLoading({isLoadingGraph: false});
            }
        })()
    };

    const updateTimeInterval = () => {
        const newInterval = getDefaultTimeDisplay(startDate, endDate);
        if (newInterval !== graphInterval) {
            setGraphInterval(getDefaultTimeDisplay(startDate, endDate))
        } else {
            fetchGraphData()
        }
    };

    const updateComparisonData = () => {
        const dataForComparison = data?.overview
        let comparisonData;
        switch (selectedComparison) {
            case 1:
                comparisonData = dataForComparison?.dayAgo?.totals
                break;
            case 7:
                comparisonData = dataForComparison?.weekAgo?.totals
                break;
            case 28:
                comparisonData = dataForComparison?.monthAgo?.totals
                break
            case 30:
                comparisonData = dataForComparison?.fullMonthAgo?.totals
                break
            case -1:
                comparisonData = dataForComparison?.specificComparisonDate?.totals
                break;
            default:
                comparisonData = dataForComparison?.selectedDates?.totals
                break;
        }
        setComparisonData({compare: selectedComparison, data: comparisonData})
    }

    // Component Lifecycle && Dependencies
    useEffect(updateTimeInterval, [startDate, endDate])
    useEffect(fetchGeneralData, [startDate, endDate, params.id])
    useEffect(fetchGraphData, [params.id, graphInterval]);
    useEffect(updateComparisonData, [data, selectedComparison]);

    if (!isLoading.isLoadingGeneral && !data) {
        return (<>    <ReportHeader breadcrumbsIcon={<Tag/>} breadcrumbs={[
            {text: "Partner Overview", link: urlService.partners()},
            {text: partnerName || "loading..."}
        ]}/> <Typography
            variant={"overline"}> Error
            Getting Partner Data</Typography></>)
    }

    const tagsAndTheirMetrics = data?.overview?.selectedDates?.tags?.reduce((accumulator, currTag) => {
        const {tag, totals} = currTag
        accumulator.push({tag, totals})
        return accumulator
    }, [])

    const compareData = data?.overview[metricsComparisonKeyMap[comparisonData?.compare]]?.tags
    return (
        <>
            <ReportHeader breadcrumbsIcon={<Tag/>} breadcrumbs={[
                {text: "Partner Overview", link: urlService.partners()},
                {text: partnerName || "loading..."}
            ]}/>
            <Grid container justifyContent={"flex-start"} alignItems={"stretch"}
                  style={{
                      marginRight: "15px",
                      filter: theme.palette.mode === 'dark' ? 'drop-shadow(0px 0px 2px lightgray)' : 'drop-shadow(0px 0px 5px lightgray)'
                  }}
                  spacing={2}>
                <>
                    {/* Row 1: Comparison (left) and Partner Info (right) */}
                    <Grid size={{ xs: 12, lg: 4 }} sx={{ flexShrink: 0, paddingLeft: { xs: 0, lg: '12px' } }}>
                        <MetricsComparison optionalComparisonDates={optionalComparisonDates} specificComparisonDate={specificComparisonDate}
                                           setSpecificComparisonDate={setSpecificComparisonDate} />
                    </Grid>
                    <Grid size={{ xs: 12, lg: 8 }} sx={{ flexShrink: 1, minWidth: 0, paddingRight: { xs: 0, lg: '12px' } }}>
                        {!isLoading.isLoadingGeneral && data ?
                            <Paper elevation={5}
                                   style={{
                                       padding: "10px 0",
                                       height: '100%',
                                       margin: '0 0 0 10px',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       borderRadius: "16px",
                                       boxShadow: "none"
                                   }}>
                                <OverviewInfo pageResult={data?.overview} />
                            </Paper> :
                            <Skeleton sx={{borderRadius: "16px"}} variant={"rounded"} height={"80px"} width={"100%"}
                                      animation={"wave"}/>}
                    </Grid>
                    
                    {/* Row 2: Metrics spanning full width */}
                    <Grid size={12} sx={{ paddingLeft: { xs: 0, lg: '12px' }, paddingRight: { xs: 0, lg: '12px' } }}>
                        {!isLoading.isLoadingGeneral && data ?
                            <Paper elevation={5}
                                   style={{
                                       padding: "10px 0",
                                       height: '100%',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       borderRadius: "16px",
                                       boxShadow: "none"
                                   }}>
                                <OverviewMetrics pageResult={data?.overview} comparisonData={comparisonData} />
                            </Paper> :
                            <Skeleton sx={{borderRadius: "16px"}} variant={"rounded"} height={"80px"} width={"100%"}
                                      animation={"wave"}/>}
                    </Grid>
                    <Grid item sx={{height: '400px', margin: '0 auto', width: {xs: '95%', sm: '96%', md: '99%'}}}>
                        <ComparisonOverviewGraph isLoading={isLoading.isLoadingGraph} graphData={graphData}
                                                 startDate={startDate}
                                                 endDate={endDate}
                                                 setNewInterval={setGraphInterval} graphIntervalIndx={graphInterval}
                        />

                    </Grid>
                    {isLoading.isLoadingGeneral ?
                        <Skeleton sx={{borderRadius: "16px", m: 5}} variant={"rounded"} animation={"wave"}
                                  height={"600px"}/> :
                        data.comboTags.selectedDates.length > 0 ?
                            <Grid item xs={12} sx={{margin: '0 10px'}}>
                                <HomeTopTagsTable tags={data.comboTags.selectedDates || []}
                                                  isLoading={isLoading.isLoadingGeneral}
                                                  compareData={data.comboTags[metricsComparisonKeyMap[comparisonData.compare]]}


                                />
                            </Grid> : <Grid></Grid>}
                    {isLoading.isLoadingGeneral ?
                        <Skeleton animation={"wave"} sx={{borderRadius: "16px", m: 5}} variant={"rounded"}
                                  height={"600px"}/> :
                        params?.id ?
                            <Grid item width={"100%"}>
                                <Typography variant={"overline"}>Aliases</Typography>
                                <DataTable baseUrl={`${partnersBaseUrl}/${params.id}/networkPartners`}
                                           data={{partnerId: params.id}}
                                           sideColumn={true} partnerColumn={false}/>
                            </Grid> : <Grid></Grid>}

                    <Grid item xs={12} style={{margin: '0 10px'}}>
                        {!isLoading.isLoadingGeneral && data ?
                            <AdserverTagsTable loading={isLoading.isLoadingGeneral} refreshFunc={fetchGeneralData}
                                               metrics={tagsAndTheirMetrics}
                                               compareData={compareData}
                            /> :
                            <Skeleton animation={"wave"} height={"600px"} sx={{borderRadius: "16px"}}
                                      variant={"rounded"}/>}
                    </Grid>
                    <Grid item sm={12}
                          sx={{height: '400px', margin: '10px auto', width: {xs: '95%', sm: '96%', md: '99%'}}}>
                        <PredictiveGraphContainer url={`${partnersBaseUrl}/overview/${params.id}/predictive-graph`}
                                                  bodyObject={{partnerId: params.id}}
                                                  organization={selectedOrganization}/>
                    </Grid>
                </>
            </Grid>
        </>)

}

export default connector(PartnerOverview);